import { exportToSvg } from "@excalidraw/utils/export";
import { useEffect, useState } from "react";

import { COLOR_PALETTE } from "@excalidraw/common";

import { atom, useAtom } from "../editor-jotai";
import { useApp } from "../components/App";

import type { LibraryItem, BinaryFiles } from "../types";

export type SvgCache = Map<LibraryItem["id"], SVGSVGElement>;

export const libraryItemSvgsCache = atom<SvgCache>(new Map());

const exportLibraryItemToSvg = async (
  elements: LibraryItem["elements"],
  files?: BinaryFiles,
) => {
  return await exportToSvg({
    elements,
    appState: {
      exportBackground: false,
      viewBackgroundColor: COLOR_PALETTE.white,
    },
    files: files || null,
    renderEmbeddables: false,
    skipInliningFonts: true,
  });
};

export const useLibraryItemSvg = (
  id: LibraryItem["id"] | null,
  elements: LibraryItem["elements"] | undefined,
  svgCache: SvgCache,
  libraryItemFiles?: BinaryFiles,
): SVGSVGElement | undefined => {
  const [svg, setSvg] = useState<SVGSVGElement>();
  const app = useApp();

  useEffect(() => {
    if (elements) {
      if (id) {
        // Try to load cached svg
        const cachedSvg = svgCache.get(id);

        if (cachedSvg) {
          setSvg(cachedSvg);
        } else {
          // When there is no svg in cache export it and save to cache
          (async () => {
            // Merge library item files with app files, prioritizing library item files
            const files = { ...app.files, ...libraryItemFiles };
            const exportedSvg = await exportLibraryItemToSvg(elements, files);
            // TODO: should likely be removed for custom fonts
            exportedSvg.querySelector(".style-fonts")?.remove();

            if (exportedSvg) {
              svgCache.set(id, exportedSvg);
              setSvg(exportedSvg);
            }
          })();
        }
      } else {
        // When we have no id (usually selected items from canvas) just export the svg
        (async () => {
          // For pending items, use app files
          const exportedSvg = await exportLibraryItemToSvg(elements, app.files);
          setSvg(exportedSvg);
        })();
      }
    }
  }, [id, elements, svgCache, setSvg, libraryItemFiles, app.files]);

  return svg;
};

export const useLibraryCache = () => {
  const [svgCache] = useAtom(libraryItemSvgsCache);

  const clearLibraryCache = () => svgCache.clear();

  const deleteItemsFromLibraryCache = (items: LibraryItem["id"][]) => {
    items.forEach((item) => svgCache.delete(item));
  };

  return {
    clearLibraryCache,
    deleteItemsFromLibraryCache,
    svgCache,
  };
};
